import { TFunction } from 'i18next';

import { Bonus } from '../../../../../types/bonus';
import { InviteFormData } from './invite-form';

export const getFirstName = (displayName: string): string => {
	return displayName.split(' ')[0];
};

export const formatReceiverNames = (names: string[], t: TFunction): string => {
	if (names.length === 0) return '';
	if (names.length === 1) return names[0];
	if (names.length === 2) return names.join(` ${t('invite_others_modal.default_values.and')} `);
	if (names.length === 3) return `${names[0]}, ${names[1]} ${t('invite_others_modal.default_values.and')} ${names[2]}`;
	return `${names.slice(0, 3).join(', ')} ${t('invite_others_modal.default_values.and_others')}`;
};

export const getUserNamesFromIds = (userIds: string[], formData: InviteFormData | null): string[] => {
	if (!formData) return [];

	return userIds
		.map((id) => {
			const user = formData.inviteList.find((u) => u.id === id);
			return user?.displayName.split(' ')[0] || null; // Get first name only
		})
		.filter(Boolean) as string[];
};

export const isVideoBonus = (bonus: Bonus): boolean => {
	const hasVideo = bonus.attachments?.length > 0 && bonus.attachments[0].category === 'video';

	const hasChildVideo = bonus.child_bonuses?.some((childBonus) => childBonus.attachments?.length > 0 && childBonus.attachments[0].category === 'video');

	return hasVideo || hasChildVideo || false;
};

export const createDefaultValues = (userName: string, bonus: Bonus | undefined, t: TFunction, receiverFirstNames?: string[]): InviteFormData => {
	if (!bonus)
		return {
			subject: '',
			title: '',
			body: '',
			inviteList: [],
		};

	// Use provided receiverFirstNames or fallback to display_name
	const receiverNameArray = receiverFirstNames || bonus.receivers.map((r) => getFirstName(r.display_name));

	const receiverNames = formatReceiverNames(receiverNameArray, t);

	const isCelebration = bonus.award_type === 'work_anniversary' || bonus.award_type === 'birthday';
	const hasVideo = isVideoBonus(bonus);

	// Determine action type based on award type and video presence
	let actionType = 'recognize';
	if (isCelebration && hasVideo) {
		actionType = bonus.award_type === 'work_anniversary' ? 'video_anniversary' : 'video_birthday';
	} else if (isCelebration) {
		actionType = 'celebrate';
	}

	//  Use names if its is an employee birthday or work anniversary or bonus to a group of named individuals
	let useNamedVersion = isCelebration || receiverNameArray.length > 0;

	// If we have a bonus with multiple recipients and subject isn't too long, use their names
	if (!isCelebration && receiverNameArray.length > 0) {
		const namedSubject = t(`invite_others_modal.default_values.subject_named_${actionType}`, { userName, receiverNames });
		const namedTitle = t(`invite_others_modal.default_values.title_named_${actionType}`, { userName, receiverNames });
		useNamedVersion = namedSubject.length <= 40 && namedTitle.length <= 40;
	}

	const subject = useNamedVersion
		? t(`invite_others_modal.default_values.subject_named_${actionType}`, { userName, receiverNames })
		: t(`invite_others_modal.default_values.subject_unnamed_${actionType}`, { userName });

	const title = useNamedVersion
		? t(`invite_others_modal.default_values.title_named_${actionType}`, { receiverNames })
		: t(`invite_others_modal.default_values.title_unnamed_${actionType}`);

	// Use the named version unless its a group send
	const body =
		receiverNameArray.length > 0
			? t(`invite_others_modal.default_values.body_named_${actionType}`, { receiverNames })
			: t(`invite_others_modal.default_values.body_unnamed_${actionType}`);

	return {
		subject,
		title,
		body,
		inviteList: [],
	};
};
